import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../config/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const TYPE_OPTIONS = [
  { value: "language_certificate", label: "Sprachnachweis" },
  { value: "education_certificate", label: "Bildungsnachweis" },
  { value: "work_reference", label: "Arbeitsnachweis" },
  { value: "professional_certificate", label: "Berufsnachweis" },
  { value: "other", label: "Weiteres" },
];

const CANDIDATE_STATUS_OPTIONS = [
  { value: "declared", label: "Eingetragen" },
  { value: "pending_review", label: "Bitte pruefen" },
];

const PLATFORM_STATUSES = new Set(["formally_checked", "rejected"]);

function createEmptyReference() {
  return {
    type: "language_certificate",
    title: "",
    issuer: "",
    source: "",
    date_from: "",
    date_to: "",
    issued_at: "",
    reference_no: "",
    reference_text: "",
    file_path: "",
    status: "declared",
    notes: "",
  };
}

function statusClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "formally_checked") return "is-checked";
  if (value === "pending_review") return "is-pending";
  if (value === "rejected") return "is-rejected";
  if (value === "uploaded") return "is-uploaded";
  return "is-default";
}

function isPlatformManagedStatus(status) {
  return PLATFORM_STATUSES.has(String(status || "").toLowerCase());
}

function isServerFilePath(value) {
  return /^\/uploads\/verification_refs\/[A-Za-z0-9._-]+$/.test(String(value || "").trim());
}

function trimString(value) {
  return String(value || "").trim();
}

function normalizeEntryForSave(entry, options = {}) {
  const payload = {
    type: trimString(entry.type),
    title: trimString(entry.title),
    issuer: trimString(entry.issuer),
    source: trimString(entry.source),
    date_from: trimString(entry.date_from),
    date_to: trimString(entry.date_to),
    issued_at: trimString(entry.issued_at),
    reference_no: trimString(entry.reference_no),
    reference_text: trimString(entry.reference_text),
    file_path: trimString(entry.file_path),
    notes: trimString(entry.notes),
  };

  if (!options.preservePlatformStatus) {
    payload.status = trimString(entry.status) || "declared";
  }

  return payload;
}

function validateEntry(entry) {
  const title = trimString(entry.title);
  const issuer = trimString(entry.issuer);
  const source = trimString(entry.source);
  const referenceNo = trimString(entry.reference_no);
  const referenceText = trimString(entry.reference_text);
  const filePath = trimString(entry.file_path);
  const dateFrom = trimString(entry.date_from);
  const dateTo = trimString(entry.date_to);
  const issuedAt = trimString(entry.issued_at);

  if (!title) return "Ein Titel ist noetig.";
  if (!dateFrom && !dateTo && !issuedAt) return "Bitte gib mindestens ein Datum an.";
  if (dateFrom && dateTo && dateFrom > dateTo) return "Der Start muss vor dem Enddatum liegen.";
  if (issuedAt && dateTo && issuedAt < dateTo) return "Das Ausstellungsdatum kann nicht vor dem Enddatum liegen.";
  if (issuedAt && dateFrom && issuedAt < dateFrom) return "Das Ausstellungsdatum kann nicht vor dem Start liegen.";
  if (!issuer && !source && !referenceNo && !referenceText && !filePath) {
    return "Fuege Aussteller, Quelle, Referenztext, Referenznummer oder eine Datei hinzu.";
  }
  if (filePath && !isServerFilePath(filePath)) {
    return "Dateien muessen aus dem sicheren Uploadpfad kommen.";
  }
  return "";
}

function candidateStatusValue(status) {
  return String(status || "").toLowerCase() === "pending_review" ? "pending_review" : "declared";
}

const CVVerificationReferencesBox = ({ userid }) => {
  const activeUserid = String(userid || localStorage.getItem("userid") || "").trim();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(createEmptyReference());
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [uploadingId, setUploadingId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, [token]);

  const loadRows = useCallback(async () => {
    if (!activeUserid) return;
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${config.API_BASE_URL}/api/cv/verification-references`, {
        headers: authHeaders,
        params: { userid: activeUserid },
      });
      setRows(res.data?.rows || []);
    } catch (err) {
      setError(err.response?.data?.message || "Nachweise konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [activeUserid, authHeaders]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const patchDraft = (key, value) => {
    setMessage("");
    setError("");
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const patchRow = (id, key, value) => {
    setMessage("");
    setError("");
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const clearRowFile = (id) => patchRow(id, "file_path", "");
  const clearDraftFile = () => patchDraft("file_path", "");

  const createReference = async () => {
    if (!activeUserid) {
      setError("Keine Applicant-ID gefunden.");
      return;
    }

    const validationError = validateEntry(draft);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSavingId("draft");
      setMessage("");
      setError("");
      const res = await axios.post(
        `${config.API_BASE_URL}/api/cv/verification-references`,
        { userid: activeUserid, ...normalizeEntryForSave(draft) },
        { headers: authHeaders }
      );
      setRows((prev) => [res.data.reference, ...prev]);
      setDraft(createEmptyReference());
      setMessage("Nachweis angelegt.");
    } catch (err) {
      setError(err.response?.data?.message || "Nachweis konnte nicht angelegt werden.");
    } finally {
      setSavingId("");
    }
  };

  const updateReference = async (row) => {
    if (!row?.id) return;

    const validationError = validateEntry(row);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSavingId(String(row.id));
      setMessage("");
      setError("");
      const res = await axios.put(
        `${config.API_BASE_URL}/api/cv/verification-references/${row.id}`,
        {
          userid: activeUserid,
          ...normalizeEntryForSave(row, { preservePlatformStatus: isPlatformManagedStatus(row.status) }),
        },
        { headers: authHeaders }
      );
      setRows((prev) => prev.map((entry) => (entry.id === row.id ? res.data.reference : entry)));
      setMessage("Nachweis aktualisiert.");
    } catch (err) {
      setError(err.response?.data?.message || "Nachweis konnte nicht aktualisiert werden.");
    } finally {
      setSavingId("");
    }
  };

  const deleteReference = async (id) => {
    if (!id) return;
    try {
      setSavingId(String(id));
      setMessage("");
      setError("");
      await axios.delete(`${config.API_BASE_URL}/api/cv/verification-references/${id}`, {
        headers: authHeaders,
        params: { userid: activeUserid },
      });
      setRows((prev) => prev.filter((row) => row.id !== id));
      setMessage("Nachweis entfernt.");
    } catch (err) {
      setError(err.response?.data?.message || "Nachweis konnte nicht entfernt werden.");
    } finally {
      setSavingId("");
    }
  };

  const uploadFile = async (targetId, file, onUploaded) => {
    if (!file) return;
    try {
      setUploadingId(targetId);
      setMessage("");
      setError("");
      const formData = new FormData();
      formData.append("userid", activeUserid);
      formData.append("file", file);

      const headers = { ...authHeaders, "Content-Type": "multipart/form-data" };
      const res = await axios.post(`${config.API_BASE_URL}/api/cv/verification-references/upload`, formData, { headers });
      onUploaded(res.data?.filePath || "");
      setMessage("Datei hochgeladen.");
    } catch (err) {
      setError(err.response?.data?.message || "Datei konnte nicht hochgeladen werden.");
    } finally {
      setUploadingId("");
    }
  };

  return (
    <div className="applicant-tool-card applicant-tool-card--references">
      <div className="applicant-tool-card__head">
        <div>
          <span className="applicant-section-kicker">Nachweise</span>
          <h4>Dokumente und Referenzen</h4>
        </div>
      </div>

      <div className="applicant-tool-card__intro">
        Trage hier Nachweise ein, die deine Bewerbung staerken. Das ist kein Pruefcockpit, sondern dein eigener Nachweisbereich.
      </div>

      <div className="applicant-tool-card__body applicant-tool-card__body--stack">
        <div className="applicant-reference-editor">
          <h4>Neuen Nachweis anlegen</h4>
          <div className="applicant-reference-grid">
            <div>
              <Label className="applicant-workspace-label">Typ</Label>
              <Select value={draft.type} onValueChange={(value) => patchDraft("type", value)}>
                <SelectTrigger className="applicant-field">
                  <SelectValue placeholder="Typ waehlen" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="applicant-workspace-label">Dein Status</Label>
              <Select value={candidateStatusValue(draft.status)} onValueChange={(value) => patchDraft("status", value)}>
                <SelectTrigger className="applicant-field">
                  <SelectValue placeholder="Status waehlen" />
                </SelectTrigger>
                <SelectContent>
                  {CANDIDATE_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="applicant-workspace-label">Titel</Label>
              <Input value={draft.title} onChange={(e) => patchDraft("title", e.target.value)} className="applicant-field" />
            </div>
            <div>
              <Label className="applicant-workspace-label">Aussteller</Label>
              <Input value={draft.issuer} onChange={(e) => patchDraft("issuer", e.target.value)} className="applicant-field" />
            </div>
            <div>
              <Label className="applicant-workspace-label">Quelle</Label>
              <Input value={draft.source} onChange={(e) => patchDraft("source", e.target.value)} className="applicant-field" />
            </div>
            <div>
              <Label className="applicant-workspace-label">Referenznummer</Label>
              <Input value={draft.reference_no} onChange={(e) => patchDraft("reference_no", e.target.value)} className="applicant-field" />
            </div>
            <div>
              <Label className="applicant-workspace-label">Ausgestellt am</Label>
              <Input type="date" value={draft.issued_at || ""} onChange={(e) => patchDraft("issued_at", e.target.value)} className="applicant-field" />
            </div>
            <div className="applicant-reference-grid applicant-reference-grid--dates">
              <div>
                <Label className="applicant-workspace-label">Von</Label>
                <Input type="date" value={draft.date_from || ""} onChange={(e) => patchDraft("date_from", e.target.value)} className="applicant-field" />
              </div>
              <div>
                <Label className="applicant-workspace-label">Bis</Label>
                <Input type="date" value={draft.date_to || ""} onChange={(e) => patchDraft("date_to", e.target.value)} className="applicant-field" />
              </div>
            </div>
          </div>

          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Kurzbeschreibung</Label>
            <Textarea rows={3} value={draft.reference_text} onChange={(e) => patchDraft("reference_text", e.target.value)} className="applicant-field" />
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Deine Notiz</Label>
            <Textarea rows={2} value={draft.notes} onChange={(e) => patchDraft("notes", e.target.value)} className="applicant-field" />
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Datei</Label>
            <Input value={draft.file_path || ""} readOnly placeholder="Noch keine Datei hochgeladen" className="applicant-field" />
            <div className="applicant-reference-actions">
              <Label className="applicant-chip-button applicant-chip-button-upload">
                {uploadingId === "draft" ? "Laedt hoch ..." : "Datei hochladen"}
                <input
                  type="file"
                  accept=".pdf,image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadFile("draft", e.target.files?.[0], (filePath) => patchDraft("file_path", filePath))}
                />
              </Label>
              {draft.file_path ? (
                <Button type="button" size="sm" variant="outline" className="applicant-chip-button applicant-chip-button-danger" onClick={clearDraftFile}>
                  Datei entfernen
                </Button>
              ) : null}
              <Button onClick={createReference} disabled={savingId === "draft"} className="applicant-cta-button">
                {savingId === "draft" ? "Speichert..." : "Nachweis anlegen"}
              </Button>
            </div>
          </div>
        </div>

        <div className="applicant-reference-stack">
          <div className="applicant-tool-card__head applicant-tool-card__head--compact">
            <div>
              <span className="applicant-section-kicker">Schon eingetragen</span>
              <h4>Deine Nachweise</h4>
            </div>
            <span className="applicant-count-pill">{rows.length}</span>
          </div>

          {loading ? <p className="applicant-feedback-text">Nachweise werden geladen ...</p> : null}

          {!loading && rows.length === 0 ? (
            <div className="applicant-empty-card">
              Noch kein Nachweis eingetragen.
            </div>
          ) : null}

          {rows.map((row) => {
            const platformManaged = isPlatformManagedStatus(row.status);
            return (
              <div key={row.id} className="applicant-reference-item">
                <div className="applicant-reference-item__head">
                  <div className="applicant-reference-item__status">
                    <span className={`applicant-status-badge ${statusClass(row.status)}`}>{row.status}</span>
                    <small>#{row.id}</small>
                  </div>
                  <div className="applicant-reference-actions">
                    <Button size="sm" variant="outline" className="applicant-chip-button" onClick={() => updateReference(row)} disabled={savingId === String(row.id)}>
                      {savingId === String(row.id) ? "Speichert..." : "Speichern"}
                    </Button>
                    <Button size="sm" variant="outline" className="applicant-chip-button applicant-chip-button-danger" onClick={() => deleteReference(row.id)} disabled={savingId === String(row.id)}>
                      Loeschen
                    </Button>
                  </div>
                </div>

                {platformManaged ? (
                  <p className="applicant-feedback-text is-warning">
                    Der Plattform-Status ist schreibgeschuetzt. Wenn du Inhalte aenderst, geht der Nachweis wieder in deinen eigenen Bearbeitungsstand zurueck.
                  </p>
                ) : null}

                <div className="applicant-reference-grid">
                  <div>
                    <Label className="applicant-workspace-label">Typ</Label>
                    <Select value={row.type} onValueChange={(value) => patchRow(row.id, "type", value)}>
                      <SelectTrigger className="applicant-field">
                        <SelectValue placeholder="Typ waehlen" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Dein Status</Label>
                    <Select value={candidateStatusValue(row.status)} onValueChange={(value) => patchRow(row.id, "status", value)}>
                      <SelectTrigger className="applicant-field">
                        <SelectValue placeholder="Status waehlen" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANDIDATE_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Titel</Label>
                    <Input value={row.title || ""} onChange={(e) => patchRow(row.id, "title", e.target.value)} className="applicant-field" />
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Aussteller</Label>
                    <Input value={row.issuer || ""} onChange={(e) => patchRow(row.id, "issuer", e.target.value)} className="applicant-field" />
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Quelle</Label>
                    <Input value={row.source || ""} onChange={(e) => patchRow(row.id, "source", e.target.value)} className="applicant-field" />
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Referenznummer</Label>
                    <Input value={row.reference_no || ""} onChange={(e) => patchRow(row.id, "reference_no", e.target.value)} className="applicant-field" />
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Ausgestellt am</Label>
                    <Input type="date" value={row.issued_at || ""} onChange={(e) => patchRow(row.id, "issued_at", e.target.value)} className="applicant-field" />
                  </div>
                  <div className="applicant-reference-grid applicant-reference-grid--dates">
                    <div>
                      <Label className="applicant-workspace-label">Von</Label>
                      <Input type="date" value={row.date_from || ""} onChange={(e) => patchRow(row.id, "date_from", e.target.value)} className="applicant-field" />
                    </div>
                    <div>
                      <Label className="applicant-workspace-label">Bis</Label>
                      <Input type="date" value={row.date_to || ""} onChange={(e) => patchRow(row.id, "date_to", e.target.value)} className="applicant-field" />
                    </div>
                  </div>
                </div>

                <div className="applicant-workspace-field">
                  <Label className="applicant-workspace-label">Kurzbeschreibung</Label>
                  <Textarea rows={3} value={row.reference_text || ""} onChange={(e) => patchRow(row.id, "reference_text", e.target.value)} className="applicant-field" />
                </div>
                <div className="applicant-workspace-field">
                  <Label className="applicant-workspace-label">Deine Notiz</Label>
                  <Textarea rows={2} value={row.notes || ""} onChange={(e) => patchRow(row.id, "notes", e.target.value)} className="applicant-field" />
                </div>
                <div className="applicant-workspace-field">
                  <Label className="applicant-workspace-label">Datei</Label>
                  <Input value={row.file_path || ""} readOnly placeholder="Noch keine Datei hochgeladen" className="applicant-field" />
                  <div className="applicant-reference-actions">
                    <Label className="applicant-chip-button applicant-chip-button-upload">
                      {uploadingId === String(row.id) ? "Laedt hoch ..." : "Datei hochladen"}
                      <input
                        type="file"
                        accept=".pdf,image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => uploadFile(String(row.id), e.target.files?.[0], (filePath) => patchRow(row.id, "file_path", filePath))}
                      />
                    </Label>
                    {row.file_path ? (
                      <Button type="button" size="sm" variant="outline" className="applicant-chip-button applicant-chip-button-danger" onClick={() => clearRowFile(row.id)}>
                        Datei entfernen
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {message ? <p className="applicant-feedback-text is-success">{message}</p> : null}
        {error ? <p className="applicant-feedback-text is-error">{error}</p> : null}
      </div>
    </div>
  );
};

export default CVVerificationReferencesBox;
